import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "react-router-dom"
import clsx from "clsx"
import GameScreenLayout from "@/components/GameScreenLayout"
import {
  deleteGameNotification,
  getGameNotifications,
  markGameNotificationRead,
} from "@/api/gameNotificationsApi"
import {
  openResolvedGameNotificationLink,
  resolveGameNotificationLink,
} from "@/utils/gameNotificationLink"

export default function NotificationsPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const q = useQuery({
    queryKey: ["game-notifications"],
    queryFn: getGameNotifications,
  })

  const markRead = useMutation({
    mutationFn: (id: number) => markGameNotificationRead(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["game-notifications"] })
    },
  })

  const remove = useMutation({
    mutationFn: (id: number) => deleteGameNotification(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["game-notifications"] })
    },
  })

  const items = q.data?.items ?? []

  return (
    <GameScreenLayout title="Уведомления" backTo="/play">
      {q.isLoading ? (
        <p className="text-sm text-guardians-muted">Загрузка…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-guardians-muted">Пока пусто</p>
      ) : (
        <ul className="space-y-3">
          {items.map((item) => {
            const resolved = resolveGameNotificationLink(item.link)
            const hasLink = resolved.kind !== "none"
            return (
              <li
                key={item.notificationId}
                role="button"
                tabIndex={0}
                onClick={() =>
                  navigate("/play/notifications/detail", {
                    state: {
                      notificationId: item.notificationId,
                      title: item.title,
                      body: item.body,
                      link: item.link,
                      date: item.date,
                      from: item.from,
                    },
                  })
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    navigate("/play/notifications/detail", {
                      state: {
                        notificationId: item.notificationId,
                        title: item.title,
                        body: item.body,
                        link: item.link,
                        date: item.date,
                        from: item.from,
                      },
                    })
                  }
                }}
                className={clsx(
                  "cursor-pointer rounded-xl border p-3",
                  item.isReaded ? "border-white/10 bg-white/5" : "border-guardians-accent/30 bg-guardians-accent/5"
                )}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{item.title}</p>
                    <p className="mt-1 whitespace-pre-wrap text-xs text-guardians-muted">
                      {item.body}
                    </p>
                    <p className="mt-1 text-[10px] text-guardians-muted">
                      {new Date(item.date).toLocaleString("ru-RU")}
                    </p>
                  </div>
                  {!item.isReaded ? (
                    <span className="h-2 w-2 shrink-0 rounded-full bg-guardians-accent" />
                  ) : null}
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {hasLink ? (
                    <button
                      type="button"
                      className="rounded-lg border border-white/20 px-2 py-1 text-xs"
                      onClick={(e) => {
                        e.stopPropagation()
                        if (!item.isReaded) markRead.mutate(item.notificationId)
                        openResolvedGameNotificationLink(resolved)
                      }}
                    >
                      Открыть
                    </button>
                  ) : null}
                  {!item.isReaded ? (
                    <button
                      type="button"
                      className="rounded-lg border border-white/20 px-2 py-1 text-xs"
                      onClick={(e) => {
                        e.stopPropagation()
                        markRead.mutate(item.notificationId)
                      }}
                    >
                      Прочитано
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="rounded-lg border border-red-500/30 px-2 py-1 text-xs text-red-200"
                    onClick={(e) => {
                      e.stopPropagation()
                      remove.mutate(item.notificationId)
                    }}
                  >
                    Удалить
                  </button>
                </div>
              </li>
            )
          })}
        </ul>
      )}
    </GameScreenLayout>
  )
}
